import { toast } from 'sonner'
import type { ResolvedRequestOptions } from '@/client/client/types.gen'
import { client } from '@/client/client.gen'
import { getValidRequestBody } from '@/client/core/utils.gen'
import { isPublicAuthPage } from '@/utils/publicAuthPath'
import { reportAuthLogout } from './authLogoutReport'
import { getLogoutReasonFromError, isUserActionLogoutReason, type LogoutReason } from './authTypes'
import { refreshAccessToken } from './tokenRefresh'

export type { LogoutReason } from './authTypes'

let logoutOverlayTriggered = false

export const resetLogoutOverlayTriggered = () => {
	logoutOverlayTriggered = false
}

const triggerLogoutOverlay = (
	reason: LogoutReason,
	source: 'http_response_interceptor' | 'http_error_interceptor',
	extra?: Record<string, unknown>
) => {
	if (logoutOverlayTriggered) {
		return
	}
	logoutOverlayTriggered = true
	reportAuthLogout(reason, source, extra)
	const event = new CustomEvent<LogoutReason>('logout-required', {
		detail: reason,
	})
	window.dispatchEvent(event)
}

export const initClient = () => {
	// Configure the client
	client.setConfig({
		baseUrl: window.RUNTIME_CONFIG?.API_BASE_URL,
		throwOnError: true, // Ensure 4xx/5xx responses throw errors like Axios
		credentials: 'include',
	})

	// Response interceptor: Handle 401 and Token Refresh
	client.interceptors.response.use(async (response, request, opts) => {
		// Check if unauthorized
		if (response.status === 401) {
			// Do not treat login/2FA 401 as session expiry - let the login page show wrong credentials
			if (request.url.includes('auth/login') || request.url.includes('auth/2fa/login')) {
				return response
			}

			// On public auth pages, /auth/me 401 is expected when logged out — do not refresh or overlay
			if (isPublicAuthPage()) {
				return response
			}

			// Check for specific logout reasons (before trying refresh)
			try {
				const errorData = await response
					.clone()
					.json()
					.catch(() => null)
				if (errorData) {
					const reason = getLogoutReasonFromError(errorData)
					if (isUserActionLogoutReason(reason)) {
						triggerLogoutOverlay(reason, 'http_response_interceptor', { url: request.url })
						return response
					}
				}
			} catch {
				// If we can't parse the error, continue to refresh logic
			}

			// Prevent infinite loop if refresh token itself is invalid
			if (request.url.includes('/auth/refresh')) {
				return response
			}

			const refreshResult = await refreshAccessToken()

			if (refreshResult.ok) {
				const requestOptions = opts as ResolvedRequestOptions
				const retryBody = getValidRequestBody(requestOptions) as BodyInit | null | undefined
				const retryRequest = new Request(request.url, {
					method: request.method,
					headers: request.headers,
					body: retryBody,
					redirect: request.redirect,
					referrer: request.referrer,
					referrerPolicy: request.referrerPolicy,
					signal: request.signal,
					credentials: 'include',
				})
				const retryResponse = await fetch(retryRequest)
				if (retryResponse.ok) {
					return retryResponse
				}
			} else if (isUserActionLogoutReason(refreshResult.logoutReason)) {
				triggerLogoutOverlay(refreshResult.logoutReason, 'http_response_interceptor', {
					url: request.url,
				})
				return response
			}

			// If we get here, refresh failed or no refresh token
			triggerLogoutOverlay('session_expired', 'http_response_interceptor', { url: request.url })
		}

		return response
	})

	// Error interceptor: Show toasts for 403 errors and other unhandled errors
	client.interceptors.error.use(async (error, response, request) => {
		// Check if it's a 401 Unauthorized error
		const status401 = response?.status === 401 || (error instanceof Response && error.status === 401)
		if (status401) {
			// On public auth pages, 401 is expected when logged out — do not show logout overlay
			if (isPublicAuthPage()) {
				return error
			}

			if (logoutOverlayTriggered) {
				return error
			}

			// With throwOnError=true, error is already the parsed JSON response
			const reason =
				error && typeof error === 'object' && !(error instanceof Response)
					? getLogoutReasonFromError(error)
					: 'session_expired'

			triggerLogoutOverlay(reason, 'http_error_interceptor', {
				requestUrl: request?.url,
			})
			return error
		}

		// Parse structured error detail
		const errorDetail =
			error && typeof error === 'object' && !(error instanceof Response)
				? (error as { detail?: { code?: string; message?: string } | string }).detail
				: undefined

		const errorCode =
			typeof errorDetail === 'object' && errorDetail !== null ? errorDetail.code : undefined

		const status403 = response?.status === 403 || (error instanceof Response && error.status === 403)
		const status429 = response?.status === 429 || (error instanceof Response && error.status === 429)

		if (status403 || status429) {
			switch (errorCode) {
				case 'permission_denied':
					toast.error('You do not have permission to perform this action.')
					break
				case 'license_insufficient':
					toast.error('This feature requires a higher plan. Please upgrade to continue.')
					break
				case 'account_locked': {
					const msg =
						typeof errorDetail === 'object' && errorDetail?.message
							? errorDetail.message
							: 'Your account is locked. Please renew your subscription to regain access.'
					toast.error(msg)
					break
				}
				case 'license_limit_exceeded':
					toast.error('You have reached your plan limit. Please upgrade to continue.')
					break
				case 'rate_limit_exceeded':
					toast.error('Too many failed attempts. Please try again later.')
					break
				default:
					toast.error('Access denied. You do not have permission to perform this action.')
					break
			}
			return error
		}

		return error
	})
}
